import React, { useMemo } from "react";
import { useDepartments } from "@/contexts/DepartmentsContext";

/**
 * Compact card showing — for each department — which users have access to it.
 * Lets a manager scan coverage at a glance ("Who can take from Milling?").
 *
 * Props:
 *   users: Array of user objects (with departments[] or legacy department)
 */
const DepartmentRoster = ({ users = [] }) => {
  const { departments, colorFor } = useDepartments();

  const roster = useMemo(() => {
    return departments.map((d) => {
      const members = users.filter((u) => {
        if (!u.is_active) return false;
        if (Array.isArray(u.departments)) return u.departments.includes(d.name);
        return u.department === d.name;
      });
      // Surface managers separately — they always have access by role
      const managers = users.filter(
        (u) =>
          u.is_active &&
          (u.role === "warehouse_manager" || u.role === "operations_manager"),
      );
      return { dept: d, members, managers };
    });
  }, [departments, users]);

  if (departments.length === 0) return null;

  return (
    <div className="nb-card p-5 mt-6" data-testid="department-roster">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow">Coverage</div>
          <h3 className="font-display text-xl">Department roster</h3>
        </div>
        <div className="label-eyebrow text-[#607087]">
          Managers see all departments
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {roster.map(({ dept, members, managers }) => {
          const accent = colorFor(dept.name);
          return (
            <div
              key={dept.id || dept.name}
              data-testid={`roster-${dept.name}`}
              className="nb-card-sm p-3"
              style={{ borderColor: accent, background: `${accent}0D` }}
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="font-display text-base font-bold"
                  style={{ color: accent }}
                >
                  {dept.name}
                </div>
                <div
                  className="font-mono-num text-xs px-2 py-0.5 rounded-full border"
                  style={{ borderColor: accent, color: accent }}
                >
                  {members.length} staff
                </div>
              </div>
              {members.length === 0 ? (
                <div className="text-xs italic text-[#9A6F00]">
                  No staff assigned — only managers can access.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {members.map((m) => (
                    <span
                      key={m.id}
                      data-testid={`roster-${dept.name}-${m.name}`}
                      className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-white border-2"
                      style={{ borderColor: accent, color: "#001740" }}
                    >
                      {m.name}
                    </span>
                  ))}
                </div>
              )}
              {managers.length > 0 && (
                <div className="label-eyebrow mt-2 text-[#607087]">
                  + {managers.length} manager{managers.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DepartmentRoster;
